import type { ReportViewScopeParams } from "./Views/reportViewScopeQuery";

export type ReportViewKey = string;

export const BUILTIN_REPORT_VIEW_KEYS = {
  ALL: "all",
  PINNED: "pinned",
  OPEN: "open",
  CLOSED: "closed",
  CREATED_BY_ME: "created_by_me",
  GUEST: "guest",
  TRANSFERRED: "transferred",
} as const;

export type BuiltinReportViewKey = typeof BUILTIN_REPORT_VIEW_KEYS[keyof typeof BUILTIN_REPORT_VIEW_KEYS];

const BUILTIN_SET = new Set<string>(Object.values(BUILTIN_REPORT_VIEW_KEYS));

/**
 * Only for when the effective views (which carry the API's default view) could not be loaded:
 * the fixed built-in that the API also falls back to.
 */
export const REPORT_VIEW_FALLBACK_KEY: ReportViewKey = BUILTIN_REPORT_VIEW_KEYS.ALL;

export function isBuiltinReportViewKey(value: string): value is BuiltinReportViewKey {
  return BUILTIN_SET.has(value);
}

export function isKnownReportViewKey(value: unknown, enabledKeys?: Iterable<string>): value is ReportViewKey {
  if (typeof value !== "string" || value === "") {
    return false;
  }
  if (enabledKeys) {
    return [...enabledKeys].includes(value);
  }
  return isBuiltinReportViewKey(value);
}

/** @deprecated Use ReportViewKey + scope query params. Kept for permission shortcut mapping during migration. */
export enum ReportListType {
  TYPE_ALL = "all",
  TYPE_OPEN = "open",
  TYPE_CLOSED = "closed",
  TYPE_CREATED_BY_ME = "created-by-me",
  TYPE_GUEST = "guest",
  TYPE_TRANSFERRED = "transferred",
  TYPE_REPORT_TOPIC = "topic",
  TYPE_REPORT_SORT = "sort",
  TYPE_PER_PROPERTY = "property",
}

export function reportViewRequiresScopeParam(
  requiredScope: "reportList" | "sort" | "property" | null,
  scope: ReportViewScopeParams,
): boolean {
  if (!requiredScope) {
    return false;
  }
  if (requiredScope === "reportList") {
    return scope.topic != null;
  }
  if (requiredScope === "sort") {
    return scope.sort != null;
  }
  if (requiredScope === "property") {
    return scope.property != null;
  }
  return false;
}

export function isStatusScopedReportViewKey(key: ReportViewKey | null | undefined): boolean {
  return key === BUILTIN_REPORT_VIEW_KEYS.OPEN
    || key === BUILTIN_REPORT_VIEW_KEYS.CLOSED
    || key === BUILTIN_REPORT_VIEW_KEYS.TRANSFERRED;
}
